import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGetProfile } from '../hooks/useProfile.ts';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios.ts';

const DownloadAll = () => {
  const navigate = useNavigate();
  const { data: profile } = useGetProfile();
  
  // Fetch all members
  const { data: members, isLoading: isMembersLoading } = useQuery({
    queryKey: ['members'],
    queryFn: async () => {
      const { data } = await api.get('/members');
      return data;
    }
  });

  // Get stored filters from localStorage
  const storedFilterFromId = localStorage.getItem('id_filter_from') || '';
  const storedFilterToId = localStorage.getItem('id_filter_to') || '';

  // Filter members by stored ID range
  const filteredMembers = members?.data?.filter((member) => {
    const memberId = parseInt(member.id);
    const fromId = storedFilterFromId ? parseInt(storedFilterFromId) : 0;
    const toId = storedFilterToId ? parseInt(storedFilterToId) : Infinity;
    
    return memberId >= fromId && memberId <= toId;
  }) || [];

  const handlePrint = () => {
    const memberCount = filteredMembers.length;
    const totalAmount = 500 * memberCount;
    
    const confirmed = window.confirm(
      `Have these ID cards been paid for?\n\n` +
      `Total Amount: KES ${totalAmount.toLocaleString()}\n` +
      `Number of Members: ${memberCount}\n` +
      `Rate: KES 500 per card\n\n` +
      `Click OK to proceed with printing.`
    );
    
    if (confirmed) {
      window.print();
    }
  };

  if (isMembersLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <div className="text-center">
          <div className="mx-auto w-8 h-8 rounded-full border-b-2 border-green-600 animate-spin"></div>
          <p className="mt-2 text-gray-600">Loading member cards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header with back button and print button - hidden when printing */}
      <div className="sticky top-0 z-10 p-4 bg-white border-b print:hidden">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/id')}
              className="flex items-center px-4 py-2 text-gray-600 transition-colors hover:text-gray-800 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to ID Cards
            </button>
            {(storedFilterFromId || storedFilterToId) && (
              <div className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                Filtered: {storedFilterFromId || 'All'} - {storedFilterToId || 'All'}
              </div>
            )}
          </div>
          <button
            onClick={handlePrint}
            className="px-6 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            🖨️ Print All Cards
          </button>
        </div>
      </div>

      {/* Member Cards Grid */}
      <div className="p-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 print:grid-cols-2 print:gap-4">
          {filteredMembers.map((member) => (
            <div 
              key={member.id}
              className="w-full bg-white rounded-lg shadow-lg print:shadow-none print:break-inside-avoid"
            >
              {/* ID Card Preview */}
              <div className="flex justify-center p-4">
                <div
                  className="relative rounded-lg shadow-xl"
                  style={{
                    width: '300px',
                    height: '189px'
                  }}
                >
                  {/* Front Side */}
                  <div className="flex overflow-hidden absolute inset-0 justify-center items-center bg-white rounded-lg">
                    <img
                      src="/ID/4.png"
                      alt="ID Card Front"
                      className="w-full max-h-full"
                    />
                    <h4 className="absolute capitalize top-[40.3%] left-center w-[41%] overflow-hidden mr-[8%] text-base font-bold text-gray-800 -translate-x-1/2 -translate-y-1/2">
                      {member.name || 'Unknown'}
                    </h4>
                    <span className='absolute capitalize text-xs top-[45.3%] left-center w-[41%] overflow-hidden mr-[8%]'>
                      <span className="font-medium"></span> {member.current_group?.name || profile?.data?.current_group?.name || 'General'}
                    </span>
                    <span className='absolute text-xs top-[52.2%] left-center w-[41%] overflow-hidden mr-[8%]'>
                      <span className="font-medium"></span> {member.id}
                    </span>
                    <span className='absolute text-xs top-[58.9%] left-center w-[41%] overflow-hidden mr-[8%]'>
                      <span className="font-medium"></span> {member.phone_number || 'N/A'}
                    </span>
                    <div className="absolute flex-shrink-0 top-[30.9%] right-center w-[41%] overflow-hidden ml-[57.5%]">
                      <div className="relative">
                        {(() => {
                          // Check for stored image first, then fallback to member.profile_image
                          const storedImage = localStorage.getItem(`member_image_${member.id}`);
                          const imageToShow = storedImage || member.profile_image;
                          
                          return imageToShow ? (
                            <img
                              src={imageToShow}
                              alt="Profile"
                              className="object-cover w-[102px] h-[89px] rounded-full border-4 border-indigo-100"
                            />
                          ) : (
                            <div className="flex justify-center items-center w-[99px] h-[89px] bg-gray-200 rounded-full border-4 border-indigo-100">
                              <div className="w-9 h-9 text-gray-400">👤</div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {/* Extra card showing the back side */}
          <div className="w-full bg-white rounded-lg shadow-lg print:shadow-none print:break-inside-avoid">
            <div className="flex justify-center p-4">
              <div
                className="relative rounded-lg shadow-xl"
                style={{
                  width: '300px',
                  height: '189px'
                }}
              >
                {/* Back Side Card */}
                <div className="flex overflow-hidden absolute inset-0 justify-center items-center bg-white rounded-lg">
                  <img
                    src="/ID/3.png"
                    alt="ID Card Back"
                    className="w-full max-h-full"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 text-center">
              <p className="text-sm text-gray-600 font-medium">Card Back Template</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style jsx>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
            background: white;
          }
          
          .print\\:grid-cols-2 {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .print\\:gap-4 {
            gap: 1rem;
          }
          
          .print\\:break-inside-avoid {
            break-inside: avoid;
          }
          
          .print\\:shadow-none {
            box-shadow: none;
          }
        }
      `}</style>
    </div>
  );
};

export default DownloadAll;
